import { useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@repo/ui";
import { useEvaluatorAuthStore } from "@repo/auth";
import { AlertCircle, Lock } from "lucide-react";

export function EvaluateurGuard() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { token, setToken, setFirstName, setEmail, setParcoursEvaluateurId } =
    useEvaluatorAuthStore();

  // Token depuis l'URL (lien personnel `?acces=`), l'état de navigation
  // (pré-auth) ou le store.
  //
  // L'URL PASSE EN PREMIER, avant le store : un lien personnel ouvert dans un
  // navigateur qui porte déjà la session d'un autre évaluateur doit ouvrir SON
  // parcours, pas afficher un refus. Le token reste vérifié côté serveur.
  const accesUrl =
    new URLSearchParams(location.search).get("acces")?.trim() || undefined;
  const preAuthToken = (location.state as { token?: string } | null)?.token;
  const [currentToken, setCurrentToken] = useState<string | undefined>(
    accesUrl || preAuthToken || token || undefined,
  );
  const [error, setError] = useState<string | undefined>();

  // Métadonnées d'abord (sans auth) pour savoir si l'accès est anonyme
  const metadata = useQuery(
    api.notation.portail.obtenirMetadonneesEvaluateur,
    id ? { parcoursEvaluateurId: id as Id<"parcoursEvaluateur"> } : "skip",
  );

  const estAnonyme = metadata?.anonyme === true;

  // Requête de vérification d'accès
  const result = useQuery(
    api.notation.portail.verifierAccesEvaluateur,
    id && currentToken
      ? {
          parcoursEvaluateurId: id as Id<"parcoursEvaluateur">,
          identifiant: currentToken,
        }
      : "skip",
  );

  // Sync currentToken depuis le store après réhydratation Zustand (async)
  useEffect(() => {
    if (!currentToken && token) {
      setCurrentToken(token);
    }
  }, [token, currentToken]);

  // Persiste les valeurs quand la validation réussit
  useEffect(() => {
    if (result && result.statut === "ok" && id && currentToken) {
      setToken(currentToken);
      setParcoursEvaluateurId(id);
      if (result.prenomEvaluateur) setFirstName(result.prenomEvaluateur);
      if (result.emailEvaluateur) setEmail(result.emailEvaluateur);
      setError(undefined);
    }
  }, [
    result,
    id,
    currentToken,
    setToken,
    setParcoursEvaluateurId,
    setFirstName,
    setEmail,
  ]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const value = formData.get("identifiant") as string;
    if (value?.trim()) {
      setError(undefined);
      setCurrentToken(value.trim());
    }
  };

  // Pas d'ID dans l'URL
  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Lien invalide</h1>
          <p className="mt-2 text-muted-foreground">
            Le lien d'accès est invalide ou incomplet.
          </p>
        </div>
      </div>
    );
  }

  // Chargement des métadonnées
  if (metadata === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const label = estAnonyme ? "Token d'accès" : "Nom de famille";
  const placeholder = estAnonyme
    ? "Entrez votre token (ex: abc-1234)"
    : "Entrez votre nom de famille";
  const autoComplete = estAnonyme ? "off" : "family-name";

  // Pas encore de token - afficher la modale
  if (!currentToken) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Vérification d'identité</CardTitle>
            <CardDescription>
              {estAnonyme
                ? "Pour accéder à votre espace d'évaluation, veuillez saisir le token qui vous a été communiqué."
                : "Pour accéder à votre espace d'évaluation, veuillez saisir votre nom de famille tel qu'il a été enregistré."}
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifiant">{label}</Label>
                <Input
                  id="identifiant"
                  name="identifiant"
                  type="text"
                  placeholder={placeholder}
                  autoFocus
                  autoComplete={autoComplete}
                />
              </div>
            </CardContent>

            <CardFooter className="pt-6">
              <Button type="submit" className="w-full">
                Accéder à mon espace
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  // Chargement
  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Erreur retournée par la requête
  if (result.statut === "erreur") {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Erreur d'accès</CardTitle>
            <CardDescription>{result.message}</CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifiant">{label}</Label>
                <Input
                  id="identifiant"
                  name="identifiant"
                  type="text"
                  placeholder={placeholder}
                  autoFocus
                  autoComplete={autoComplete}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-surface bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>{error}</p>
                </div>
              )}
            </CardContent>

            <CardFooter className="pt-6">
              <Button type="submit" className="w-full">
                Réessayer
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  return <Outlet />;
}
